import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ContextType } from 'api/conversations';
import { TaskId } from 'api/models';
import { ProjectGlobalConversation } from 'api/project-conversations';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { Task } from 'data/tasks/index';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useTaskDetailQuery } from '../task';
import {
  filterTaskData,
  globalConversationQueryKey,
  GlobalConversationQueryResult,
} from './utils';

export const useConversationMetaLiveUpdateInReactQuery = (
  projectId: number,
): void => {
  const queryClient = useQueryClient();
  const [taskId, setTaskId] = useState<number>();
  const {
    data: task,
    refetch,
    isFetched,
  } = useTaskDetailQuery(TaskId(projectId, taskId!), {
    enabled: typeof taskId === 'number',
  });
  const {
    taskName,
    private: taskPrivacy,
    taskId: taskIdFromQuery,
  } = task || {};

  useEffect(() => {
    if (!taskName || taskPrivacy == null) {
      return;
    }
    // whenever there is a change in task details update conversation details as well
    const queryKey = globalConversationQueryKey.byProjectId(projectId);
    const queryResult =
      queryClient.getQueryData<GlobalConversationQueryResult>(queryKey);
    const getActive = (conversation: ProjectGlobalConversation) =>
      conversation.entityId === taskIdFromQuery &&
      conversation.entityType === ContextType.TASK;
    const conversation: ProjectGlobalConversation | undefined = cloneDeep(
      queryResult?.conversations?.find(getActive),
    );

    if (!conversation) {
      return;
    }

    conversation.entityName = taskName;
    conversation.private = taskPrivacy;
    queryClient.setQueryData<GlobalConversationQueryResult>(queryKey, {
      conversations:
        queryResult?.conversations?.map((data) =>
          getActive(data) ? conversation : data,
        ) ?? [],
      unread: queryResult?.unread ?? 0,
    });
  }, [
    isFetched,
    projectId,
    queryClient,
    taskIdFromQuery,
    taskName,
    taskPrivacy,
  ]);

  const fetchTaskById = useCallback(
    (id: number) => {
      setTaskId(id);
      refetch();
    },
    [refetch],
  );
  const updateNameAndStatus = useCallback(
    (snap: SocketData<Task | number[]>) => {
      const socketData = filterTaskData(snap);

      if (!socketData) {
        return;
      }
      fetchTaskById(socketData.data.taskId);
    },
    [fetchTaskById],
  );

  useProjectWSUpdates(
    projectId,
    updateNameAndStatus,
    EventType(Action.Update, ObjectType.Task),
  );
};
